
import {
  defineComponent, ref, computed, watch,
} from 'vue';
import useImportWizard from '@/composables/import/useImportWizard';
import { getImportAdapter } from '@/composables/import/registry';
import { useFileParser } from '@/composables/import/useFileParser';
import type { ImportFieldMapping, ImportStepType } from '@/composables/import/types';
import { getFileTypeFromName } from '@/composables/import/adapters/base.adapter';
import { uploadImportFile } from '@/composables/import/importService';
import FileSelectStep from '@/components/import/FileSelectStep.vue';
import EditorModal from '@/components/modals/EditorModal.vue';
import ColumnMappingStep from '@/components/import/ColumnMappingStep.vue';
import ImportPreview from '@/components/import/ImportPreview.vue';
import ItemSelectorStep from '@/components/import/ItemSelectorStep.vue';
import { showToast, showToastError, showToastSuccess } from '@/helpers';
import { useI18n } from 'vue-i18n';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { UserState } from '@/composables/User';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

export default defineComponent({
  name: 'ImportModal',

  components: {
    FileSelectStep,
    EditorModal,
    ColumnMappingStep,
    ImportPreview,
    ItemSelectorStep,
    SpmButton,
    ConfirmDialog,
  },

  props: {
    title: {
      type: String,
      required: true,
    },

    importType: {
      type: String,
      required: true,
    },

    params: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['close', 'success'],

  setup(props, { emit }) {
    const adapter = getImportAdapter(props.importType);
    const wizard = useImportWizard(adapter.steps);
    const { parseFile } = useFileParser();
    const { t } = useI18n();
    const confirm = useConfirm();

    // Local state for mapping component (v-model)
    const localMapping = ref<Record<string, string>>({});

    // Track mapping validity from ColumnMappingStep
    const isMappingValid = ref(false);

    // Track item selector validity
    const isItemSelectorValid = ref(false);

    // Item selector loading and error states
    const itemSelectorLoading = ref(false);
    const itemSelectorError = ref('');

    // Loading state for file processing (between file_select and next step)
    const isProcessingFile = ref(false);

    // Track last parsed file metadata to avoid re-parsing
    const lastParsedMetadata = ref<{
      name: string;
      size: number;
      lastModified: number;
      text?: string;
    } | null>(null);

    // Direct reference to wizard state (not computed, to allow v-model writes)
    const wizardState = wizard.state;

    // Reset selected items when the uploaded file changes
    watch(() => wizardState.value.fileData.file, () => {
      wizardState.value.selectedItems = [];
    });

    // Current step configuration
    const currentStep = computed(() => wizard.currentStep.value);

    /**
     * Check if adapter has an item_selector step
     */
    const hasItemSelector = computed(() => adapter.steps.some((step) => step.type === 'item_selector'));

    // ==========================================================================
    // STEP TYPE HELPERS
    // ==========================================================================

    /**
     * Check if current step is of a specific type
     */
    const isStepType = (type: ImportStepType) => currentStep.value?.type === type;

    // ==========================================================================
    // FILE SELECT STEP HANDLERS
    // ==========================================================================

    /**
     * Get file name for display
     */
    const getFileName = () => {
      if (wizardState.value.fileData.useCopyPaste) {
        return t('import.selectStep.copyPaste.fileName');
      }
      return wizardState.value.fileData.file?.name ?? '';
    };

    /**
     * Process file or pasted data and move to next step
     */
    const processFileOrPastedData = async () => {
      try {
        let fileToProcess: File;
        let isNewData = false;
        const { fileData } = wizardState.value;

        if (fileData.useCopyPaste) {
          // Create file from pasted text (first line is always the header)
          const text = fileData.pastedText;

          // Check if content changed
          isNewData = !lastParsedMetadata.value || lastParsedMetadata.value.text !== text;

          // Use text as-is (first line = headers, rest = data)
          const blob = new Blob([text], { type: 'text/plain' });
          fileToProcess = new File([blob], 'pasted-data.txt', { type: 'text/plain' });

          if (isNewData) {
            lastParsedMetadata.value = {
              name: 'pasted-data.txt',
              size: blob.size,
              lastModified: Date.now(),
              text,
            };
          }
        } else {
          // Use uploaded file
          const f = fileData.file;
          if (!f) {
            await showToastError(t('import.file.not_selected'));
            return;
          }

          isNewData = !lastParsedMetadata.value
            || lastParsedMetadata.value.name !== f.name
            || lastParsedMetadata.value.size !== f.size
            || lastParsedMetadata.value.lastModified !== f.lastModified;

          fileToProcess = f;

          if (isNewData) {
            lastParsedMetadata.value = { name: f.name, size: f.size, lastModified: f.lastModified };
          }
        }

        // Parse the file
        const parsedData = await parseFile(fileToProcess);

        // Validate: must have at least one data row (first line is header)
        if (parsedData.rows.length === 0) {
          await showToastError(t('import.errors.noDataRows'));
          return;
        }

        wizard.state.value.parsedFile = parsedData;

        // Reset mapping if new data
        if (isNewData) {
          localMapping.value = {};
          wizard.state.value.mapping = [];
        }

        wizard.next();
      } catch (error) {
        console.error('Error processing file:', error);
        await showToastError(t('import.errors.genericError'));
      }
    };

    /**
     * Process file for item selector step (campaign_automation, campaign_bulk, template)
     * Calls adapter.validateFile to get selectable items
     */
    const processFileForItemSelector = async () => {
      const { fileData: { file }, selectedItems } = wizardState.value;

      // If items are already loaded (user came back from next step), just go to next step
      if (selectedItems && selectedItems.length > 0) {
        wizard.next();
        return;
      }

      if (!file) {
        await showToastError(t('import.file.not_selected'));
        return;
      }

      isProcessingFile.value = true;
      itemSelectorLoading.value = true;
      itemSelectorError.value = '';

      try {
        const ctx = {
          importType: props.importType,
          params: props.params,
        };

        if (!adapter.validateFile) {
          throw new Error('Adapter does not support file validation');
        }

        const result = await adapter.validateFile(ctx, file);

        if (!result.success) {
          // Handle V4 to V5 import not supported
          if (result.error === 'v4_to_v5_not_supported') {
            await showToast('info', t('import.errors.v4_to_v5_not_supported'));
            return;
          }

          // Show error and stay on step 1
          const errorKey = `import.errors.${result.error}`;
          const translatedError = t(errorKey);
          // If translation exists, use it; otherwise use the raw error message
          const errorMessage = translatedError !== errorKey ? translatedError : (result.error || t('import.errors.genericError'));
          await showToastError(errorMessage);
          return;
        }

        // Helper to proceed with import
        const proceedWithImport = () => {
          wizard.state.value.selectedItems = result.items || [];
          wizard.next();
        };

        // Check for obsolete templates and show warning if needed
        if (result.hasObsoleteTemplates) {
          confirm.require({
            message: t('import.warnings.obsoleteTemplates.message'),
            header: t('import.warnings.obsoleteTemplates.header'),
            icon: 'far fa-info-circle',
            acceptLabel: t('import.warnings.obsoleteTemplates.accept'),
            rejectLabel: t('import.warnings.obsoleteTemplates.reject'),
            acceptClass: 'p-button-success',
            rejectClass: 'p-button-secondary',
            accept: () => {
              proceedWithImport();
            },
          });
        } else {
          proceedWithImport();
        }
      } catch (error) {
        console.error('Error validating file:', error);
        await showToastError(t('import.errors.genericError'));
      } finally {
        isProcessingFile.value = false;
        itemSelectorLoading.value = false;
      }
    };

    // ==========================================================================
    // COLUMN MAPPING STEP HANDLERS
    // ==========================================================================

    /**
     * Handle mapping changes from ColumnMappingStep
     */
    const onMappingChange = (mappingDto: ImportFieldMapping[]) => {
      wizard.state.value.mapping = mappingDto;

      const requiredPushTargets = ['push_endpoint', 'push_key', 'push_token'];
      const hasCompletePushMapping = requiredPushTargets.every((target) => mappingDto.some((m) => m.target === target));

      if (!hasCompletePushMapping) {
        wizardState.value.selectedVapidKeyId = undefined;
        wizardState.value.selectedVapidKeyLabel = undefined;
      }
    };

    /**
     * Handle mapping validity changes from ColumnMappingStep
     */
    const onMappingValidChange = (isValid: boolean) => {
      isMappingValid.value = isValid;
    };

    /**
     * Handle VAPID key change from ColumnMappingStep
     */
    const onVapidKeyChange = (payload: { id: string | null; label: string | null }) => {
      wizardState.value.selectedVapidKeyId = payload.id || undefined;
      wizardState.value.selectedVapidKeyLabel = payload.label || undefined;
    };

    const shopId = computed(() => UserState.activeShop?.id);

    /**
     * Handle item selector validity changes
     */
    const onItemSelectorValidChange = (isValid: boolean) => {
      isItemSelectorValid.value = isValid;
    };

    // ==========================================================================
    // NAVIGATION HANDLERS
    // ==========================================================================

    /**
     * Check if next step is item_selector (to determine file processing mode)
     */
    const nextStepIsItemSelector = computed(() => {
      const nextIndex = wizard.state.value.currentStepIndex + 1;
      const nextStep = adapter.steps[nextIndex];
      return nextStep?.type === 'item_selector';
    });

    /**
     * Handle back button click
     */
    const handleBack = () => {
      wizard.prev();
    };

    /**
     * Handle next button click
     */
    const handleNext = async () => {
      const stepType = currentStep.value?.type;

      if (stepType === 'file_select') {
        // Check if next step is item_selector (for automation/bulk/template)
        if (nextStepIsItemSelector.value) {
          await processFileForItemSelector();
        } else {
          await processFileOrPastedData();
        }
      } else if (stepType === 'column_mapping') {
        wizard.next();
      } else if (stepType === 'item_selector') {
        wizard.next();
      }
    };

    // ==========================================================================
    // SUBMIT HANDLER
    // ==========================================================================

    /**
     * Handle form submission
     * Two-step process:
     * 1. Initialize import via adapter (calls POST /v1/job/import/init)
     * 2. Upload file to the returned uploadUrl
     */
    const handleSubmit = async () => {
      wizard.state.value.isSubmitting = true;

      try {
        const ctx = {
          importType: props.importType,
          params: props.params,
        };

        let fileToUpload: File;
        let fileType: string;
        const { fileData, parsedFile } = wizardState.value;

        // Prepare file to upload
        if (fileData.useCopyPaste) {
          if (!parsedFile) {
            showToastError(t('import.errors.genericError'));
            return;
          }

          // Convert parsed data back to CSV
          const csvContent = [
            parsedFile.headers.join(','),
            ...parsedFile.rows.map((row: Record<string, unknown>) => {
              const headers = parsedFile?.headers ?? [];
              return headers.map((header: string) => {
                const value = row[header] ?? '';
                return typeof value === 'string' && (value.includes(',') || value.includes('"'))
                  ? `"${String(value).replace(/"/g, '""')}"`
                  : value;
              }).join(',');
            }),
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv' });
          fileToUpload = new File([blob], 'pasted-data.csv', { type: 'text/csv' });
          fileType = 'csv';
        } else {
          const f = fileData.file;
          if (!f) {
            showToastError(t('import.file.not_selected'));
            return;
          }
          fileToUpload = f;
          fileType = getFileTypeFromName(f.name);
        }

        // Step 1: Initialize import via adapter
        // This calls POST /v1/job/import/init with appropriate headers and body
        let initResponse;
        try {
          initResponse = await adapter.initImport(ctx, wizardState.value, fileType);
        } catch (initError) {
          console.error('Import init error:', initError);
          const errorMessage = initError instanceof Error ? initError.message : t('import.errors.initFailed');
          showToastError(errorMessage);
          return;
        }

        // Step 2: Upload file to the provided URL
        const uploadResult = await uploadImportFile(
          initResponse.uploadUrl,
          fileToUpload,
          adapter.processingMode,
        );

        if (!uploadResult.success) {
          console.error('Import upload error:', uploadResult.error);
          showToastError(uploadResult.error);
          return;
        }

        // Success - show message based on processing mode
        let successMessage: string;
        if (adapter.processingMode === 'immediate') {
          // Immediate mode - import is done
          successMessage = t('import.done');
        } else {
          // Async mode - import has been launched
          successMessage = uploadResult.data.totalRows
            ? t('import.success.withRows', { count: uploadResult.data.totalRows })
            : t('import.success.default');
        }
        showToastSuccess(successMessage);
        emit('success');
        emit('close');
      } catch (error) {
        console.error('Error submitting import:', error);
        const errorMessage = error instanceof Error ? error.message : t('import.errors.genericError');
        showToastError(errorMessage);
      } finally {
        wizard.state.value.isSubmitting = false;
      }
    };

    // ==========================================================================
    // COMPUTED PROPERTIES
    // ==========================================================================

    /**
     * Wizard subtitle showing current step
     */
    const wizardSubtitle = computed(() => {
      const stepIndex = wizard.currentStepIndex.value + 1;
      const totalSteps = wizard.steps.value.length;
      return t('import.wizard.stepIndicator', { current: stepIndex, total: totalSteps });
    });

    /**
     * Current step description for footer
     */
    const currentStepDescription = computed(() => {
      const step = currentStep.value;
      if (!step) return '';
      return t(step.descriptionKey);
    });

    /**
     * Check if can proceed to next step - uses adapter's canProceed method
     * For column_mapping step, also check isMappingValid from ColumnMappingStep
     * For item_selector step, check isItemSelectorValid
     */
    const canProceedToNext = computed(() => {
      // For column mapping step, use the validity from ColumnMappingStep component
      if (currentStep.value?.type === 'column_mapping') {
        return isMappingValid.value;
      }

      // For item selector step, use the validity from ItemSelectorStep component
      if (currentStep.value?.type === 'item_selector') {
        return isItemSelectorValid.value;
      }

      if (adapter.canProceed) {
        return adapter.canProceed(wizard.currentStepIndex.value, wizardState.value);
      }
      // Fallback - should not happen if using createAdapter
      return true;
    });

    return {
      // Adapter
      adapter,
      // Wizard
      wizard,
      wizardState,
      currentStep,
      // Local state
      localMapping,
      itemSelectorLoading,
      itemSelectorError,
      isProcessingFile,
      // Computed
      hasItemSelector,
      wizardSubtitle,
      currentStepDescription,
      canProceedToNext,
      // Methods
      isStepType,
      getFileName,
      onMappingChange,
      onMappingValidChange,
      onVapidKeyChange,
      onItemSelectorValidChange,
      shopId,
      handleBack,
      handleNext,
      handleSubmit,
      // i18n
      t,
    };
  },
});
